/**
 * Compare objects values
 * if they are all equal return true else false
 * 
 * @param obj1 
 * @param obj2 
 * @returns 
 */
export const isObjectsEquals = (obj1: unknown, obj2: unknown): boolean => {
    if (obj1 === obj2) {
        return true;
    }

    if (obj1 == null || typeof obj1 !== 'object' || obj2 == null || typeof obj2 !== 'object') {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        // Safe property access: check if key exists in obj2 before accessing
        if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
            return false;
        }
        const value1 = Reflect.get(obj1, key);
        const value2 = Reflect.get(obj2, key);
        if (!isObjectsEquals(value1, value2)) {
            return false;
        }
    }

    return true;
}

const SAFE_PROPERTY_KEY = /^[A-Za-z0-9_.-]+$/;

export const isSafePropertyKey = (key: string): boolean =>
    typeof key === 'string' && SAFE_PROPERTY_KEY.test(key);

/**
 * Safely access object property to prevent prototype pollution
 * @param obj - The object to access
 * @param key - The property key
 * @returns The property value or undefined if not found
 */
export const safeGetProperty = <T>(obj: Record<string, T> | null | undefined, key: string): T | undefined => {
    if (obj == null || typeof obj !== 'object') {
        return undefined;
    }
    if (!isSafePropertyKey(key)) {
        return undefined;
    }
    // Use hasOwnProperty to prevent prototype pollution
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        return undefined;
    }
    return Reflect.get(obj, key);
}

export const safeSetProperty = <T>(
    obj: Record<string, T> | null | undefined,
    key: string,
    value: T
): void => {
    if (obj == null || typeof obj !== 'object' || !isSafePropertyKey(key)) {
        return;
    }
    Reflect.set(obj, key, value);
}

export const safeDeleteProperty = (
    obj: Record<string, unknown> | null | undefined,
    key: string
): void => {
    if (obj == null || typeof obj !== 'object' || !isSafePropertyKey(key)) {
        return;
    }
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
        Reflect.deleteProperty(obj, key);
    }
}